import loadable from "@loadable/component";
import { inject, observer } from "mobx-react";
import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import type { BlockFragment } from "../../generated/webapi.g";
import { blockHaveAd, showLoadMoreAd } from "../../lib/ads";
import { isCalendarPublishRecord, isFeedResult } from "../../lib/guards";
import type { Store } from "../../../../client/stores";
import { reverseRoute } from "../../lib/route";
import { removePrefix } from "../../lib/utilities";
import { Grid, Heading, MagicLink } from "../../ui";
import { HeadingProps } from "../../ui/Heading.component";
import { AdPosition } from "../Ad.component";
import "./Datasource.style.scss";
import { DatasourceProps } from "./Datasource.component";
import { NoFeedItemMessage } from "./NoFeedItemMessage.component";
import { logger } from "../../lib/logging";

const log = logger.getChild("CalendarDatasource");
const CalendarFeedItem = loadable(() => import("./CalendarFeedItem.component"));

import styles from "./CalendarDatasourceMenu.module.scss";
const CalendarDatasourceMenu = ({ store }: { store: Store }) => {
    const { t } = useTranslation();
    const { site, routeParams } = store;

    if (!site.sections.general?.default_event_categories) {
        return null;
    }

    const tags = site.sections.general?.default_event_categories ?? [];
    const allEventsUrl = reverseRoute(site, "calendar_content_all") ?? "";
    const allEventsActiveClass = !routeParams?.topic ? styles.active : "";

    return (
        <nav className={styles.componentCalendarMenu}>
            <ul>
                {allEventsUrl ? (
                    <li>
                        <MagicLink to={allEventsUrl} target={"_top"} className={allEventsActiveClass}>
                            {t("all_events")}
                        </MagicLink>
                    </li>
                ) : null}
                {tags
                    .filter(tag => tag.taxo?.displayName)
                    .map((tag, i) => {
                        if (!tag?.taxo?.memberName) {
                            return null;
                        }

                        const topic = removePrefix(/^[\w-]+\//)(tag?.taxo?.memberName);

                        const activeClass = topic === routeParams?.topic ? styles.active : "";

                        const url = reverseRoute(site, "calendar_content_category", {
                            topic,
                        });

                        if (!url) {
                            return null;
                        }

                        return (
                            <li key={i}>
                                <MagicLink to={url} target={"_top"} className={activeClass}>
                                    {tag.taxo?.displayName}
                                </MagicLink>
                            </li>
                        );
                    })}
            </ul>
        </nav>
    );
};

export const CalendarDatasource = inject("store")(
    observer((props: DatasourceProps) => {
        const { store, title, content, feedId, displayHints, block } = props;

        if (!store) {
            return null;
        }

        if (!content?.length) {
            log.info("No content");
            if (props.displayHints.hints?.includes("display-hints/message-for-no-results")) {
                return <NoFeedItemMessage />;
            }
            return null;
        }

        const heading: HeadingProps = useMemo(() => props.heading ?? { level: 2 }, [props.heading]);
        const adBlockData = useMemo(() => ({ value: { ...block.value } } as BlockFragment), [block.value]);
        const { showEyebrow, enableLoadMore } = displayHints;
        const adInsertPosition = useMemo(() => block.value.size || 10, [block.value.size]);
        const haveAds = useMemo(() => blockHaveAd(block.value.position), [block.value.position]);

        const header = useMemo(() => (title ? <Heading {...heading}>{title}</Heading> : null), [title, heading]);
        const showMenu = props.displayHints.hints?.includes("display-hints/calendar-show-menu");

        const items = useMemo(
            () =>
                content.map((item, i) => {
                    const { style } = displayHints;

                    if (!isFeedResult(item) || !isCalendarPublishRecord(item.record)) {
                        return null;
                    }

                    const showAd = showLoadMoreAd(content.length, adInsertPosition, i, enableLoadMore);
                    return (
                        <React.Fragment key={`feed-item-${feedId}-${(item as any).ref_id}-${i}`}>
                            <CalendarFeedItem item={item.record} style={style} index={i} showEyebrow={showEyebrow} />
                            {haveAds && showAd ? (
                                <div className="load-more-ad">
                                    <AdPosition
                                        block={adBlockData}
                                        batchId={`${feedId}-${Math.ceil(i / adInsertPosition)}`}
                                    />
                                </div>
                            ) : null}
                        </React.Fragment>
                    );
                }),
            [adBlockData, adInsertPosition, content, displayHints, enableLoadMore, feedId, haveAds, showEyebrow],
        );

        return (
            <React.Fragment key={block.id}>
                {header}
                <div className="feed-cards" data-block-id={block.id} suppressHydrationWarning={true}>
                    {showMenu ? <CalendarDatasourceMenu store={store} /> : null}
                    <Grid columns={displayHints.columns}>{items}</Grid>
                </div>
            </React.Fragment>
        );
    }),
);

export default CalendarDatasource;
